import * as React from 'react';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import User from '@app/Interface/UserInterface';
import { Button, Card, CardBody, Split, SplitItem, Switch } from '@patternfly/react-core';
import { UserContext } from '@app/Context/UserContext';
import UpdateChartNotificationModel from '@app/Model/UpdateChartNotificationModel';
import PageInterface from '@app/Interface/PageInterface';
import ChartNotificationHelper from '@app/Service/ChartNotificationHelper';
import UserChartNotificationSettingsModel from '@app/Model/UserChartNotificationSettingsModel';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';

interface ChartSettingCardProps {
    currentUser: User;
    setting: UserChartNotificationSettingsInterface;
    page: PageInterface|null;
    handleEditChartSetting: (show: boolean, setting: UserChartNotificationSettingsInterface, page: PageInterface|null) => void;
}

const ChartSettingCard: React.FC<ChartSettingCardProps> = ({ currentUser, setting, page, handleEditChartSetting }) => {

    const [formSettings, setFormSettings] = React.useState<UserChartNotificationSettingsInterface>(setting);

    const fieldId = `sendChartNotification${setting.chart}`;

    const { userDispatch } = React.useContext(UserContext);

    /**
     * Handler when switch is updated. 
     * 
     * @param checked 
     * @param event 
     */
    const handleOnChange = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        const fieldId = event.currentTarget.id;

        const updatedFormSettings = { ...formSettings };

        updatedFormSettings.active = checked;

        const updatedUser = { ...currentUser };

        if (formSettings.isNew) {

            const newChartNotification = new UserChartNotificationSettingsModel(updatedFormSettings);

            if (typeof updatedUser.fpuChartNotificationSettings === "boolean") {
                updatedUser.fpuChartNotificationSettings = [newChartNotification];
            } else {
                updatedUser.fpuChartNotificationSettings.push(newChartNotification);
            }

        } else {

            // find the matching setting and update it
            if (typeof updatedUser.fpuChartNotificationSettings !== "boolean") {

                updatedUser.fpuChartNotificationSettings.forEach((chartSetting, index) => {

                    if (chartSetting.fpu === setting.fpu && chartSetting.chart === setting.chart) {

                        // replace the chart setting values
                        updatedUser.fpuChartNotificationSettings[index] = updatedFormSettings;

                    }

                });

            }

        }

        // update user context
        userDispatch({
            type: "UPDATE",
            payload: updatedUser
        });

        // Save setting to middleware endpoint
        // send middleware request to update user. DON'T wait
        try {

            // no async await
            const data = new UpdateChartNotificationModel();
            data.userID = currentUser.id;
            data.fpuID = updatedFormSettings.fpu;
            data.chart = updatedFormSettings.chart;
            data.active = updatedFormSettings.active;
            data.thresholdRange = updatedFormSettings.threshold_range;
            data.thresholdMin = updatedFormSettings.threshold_value_min;
            data.thresholdMax = updatedFormSettings.threshold_value_max;

            ChartNotificationHelper.updateChartNotification(data);

        } catch (error) {
            console.group("ChartNotificationSetting update");
            console.info("checked: ", checked);
            console.info("fieldId: ", fieldId);
            console.info("error", error);
            console.groupEnd();
        }

        setFormSettings(updatedFormSettings);

    }

    let description = <></>;

    switch (setting.threshold_range) {
        case "above":
            description = <div>Notify when <strong>{setting.chart.toUpperCase()}</strong> is <strong>Higher</strong> Than {setting.threshold_value_max}.</div>;
            break;

        case "below":
            description = <div>Notify when <strong>{setting.chart.toUpperCase()}</strong> is <strong>Lower</strong> Than {setting.threshold_value_min}.</div>;
            break;

        case "outside":
            description = <div>Notify when <strong>{setting.chart.toUpperCase()}</strong> is <strong>Lower</strong> Than {setting.threshold_value_min} or is <strong>Higher</strong> Than {setting.threshold_value_max}.</div>;
            break;

        case "between":
            description = <div>Notify when <strong>{setting.chart.toUpperCase()}</strong> is <strong>Between</strong> {setting.threshold_value_min} and {setting.threshold_value_max}.</div>;
            break;

        default:
            break;
    }

    return (
        <Card className="pf-u-mb-md">
            <CardBody>
                <Split hasGutter>
                    <SplitItem isFilled>
                        <Split>

                            <SplitItem>
                                {description}
                                {(formSettings.threshold_value_min === 0 || formSettings.threshold_value_max === 0) &&
                                    <div>
                                        <ExclamationTriangleIcon color="var(--pf-global--warning-color--100)" />
                                        <span className="pf-u-warning-color-200 pf-u-pl-sm">Please add a threshold</span>
                                    </div>

                                }
                            </SplitItem>
                            <SplitItem>
                                <Button
                                    variant="link"
                                    className="pf-u-py-0"
                                    onClick={() => handleEditChartSetting(true, formSettings, page)}
                                >
                                    Edit
                                </Button>
                            </SplitItem>

                        </Split>
                    </SplitItem>
                    <SplitItem>
                        <Switch
                            id={fieldId}
                            aria-label={`Send me ${formSettings.chart} Notification`}
                            isChecked={formSettings.active}
                            onChange={handleOnChange}
                            isDisabled={formSettings.threshold_value_min === 0 || formSettings.threshold_value_max === 0}
                        />
                    </SplitItem>
                </Split>
            </CardBody>
        </Card>
    );

};

export default ChartSettingCard;